import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Reveal, Overline, SectionHeading, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getContent, getSiteContent } from "@/lib/api";
import { ArrowRight, Heartbeat, Users, Buildings, HandHeart, Handshake } from "@phosphor-icons/react";

export default function Impact() {
  const { data: stats } = useCollection(() => getContent("impact_stats"), []);
  const { data: stories } = useCollection(() => getContent("impact_stories"), []);
  const [nicci, setNicci] = useState(null);
  useEffect(() => { getSiteContent("nicci").then(setNicci).catch(() => {}); }, []);

  return (
    <div data-testid="impact-page">
      <PageHero label="Impact" title="Cancer Care Beyond the Clinic"
        intro="Dr. Sirohi's work extends into cancer awareness, community outreach, rural health, health equity and social impact."
        image="https://images.unsplash.com/photo-1654609160632-7324716196fb?crop=entropy&cs=srgb&fm=jpg" />

      <section className="bg-white py-16">
        <Container>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-3 lg:grid-cols-5">
            {stats.map((s, i) => (
              <Reveal key={s.id} delay={(i % 5) * 0.05}>
                <div className="bg-white p-8"><div className="font-serif text-4xl text-navy">{s.value}</div><div className="mt-2 text-xs uppercase tracking-wide text-softgrey-600">{s.label}</div></div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {nicci && (
        <section className="bg-navy py-20 text-white">
          <Container>
            <div className="grid gap-12 md:grid-cols-2">
              <div>
                <Overline className="text-gold">NICCI Foundation</Overline>
                <h2 className="mt-4 font-serif text-4xl tracking-tight text-white md:text-5xl">New India Cancer Charity Initiative</h2>
                <p className="mt-6 text-base leading-relaxed text-slate-300">{nicci.mission}</p>
                {nicci.note && <p className="mt-4 text-sm italic text-slate-400">{nicci.note}</p>}
                <Link to="/support" className="mt-8 inline-block"><button data-testid="nicci-support-cta" className="rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy hover:bg-gold-light">Support Our Initiatives</button></Link>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-8">
                <Overline className="text-gold">Vision</Overline>
                <p className="mt-3 font-serif text-2xl leading-snug text-white">{nicci.vision}</p>
                <div className="mt-8">
                  <Overline className="text-gold">Programmes</Overline>
                  <ul className="mt-4 space-y-3">
                    {(nicci.programmes || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Container>
        </section>
      )}

      <section className="bg-softgrey-100 py-20">
        <Container>
          <SectionHeading label="Focus Areas" title="Where we make a difference" />
          <div className="grid gap-6 md:grid-cols-3">
            {[[Heartbeat, "Community Outreach", "Village cancer camps, screening, awareness programmes and rural healthcare initiatives."],
              [Users, "Cancer Awareness", "Pink October, walkathons, public talks, school programmes and women's health initiatives."],
              [Buildings, "Global Health Equity", "Cancer care in low- and middle-income countries, capacity building, policy and advocacy."],
              [Heartbeat, "Rural Health Initiatives", "Grassroots healthcare programmes bringing cancer care to underserved villages."],
              [HandHeart, "Fundraising & Philanthropy", "Patient support, financial assistance, corporate partnerships and CSR collaborations."],
              [Handshake, "Volunteer & Partnerships", "Opportunities for individuals and organisations to join the mission against cancer."]].map(([Icon, t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-lg border border-slate-200 bg-white p-8">
                  <Icon size={28} weight="light" className="text-gold-dark" />
                  <h3 className="mt-4 font-serif text-2xl text-navy">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-softgrey-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/support"><button data-testid="impact-support-cta" className="rounded-md bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-700">Support Our Initiatives</button></Link>
            <Link to="/get-involved"><button className="rounded-md border border-navy/25 px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white">Volunteer With Us</button></Link>
            <Link to="/collaborate"><button className="rounded-md border border-navy/25 px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white">Partner With Us <ArrowRight size={14} className="inline" /></button></Link>
          </div>
        </Container>
      </section>

      {stories.length > 0 && (
        <section className="bg-white py-20">
          <Container>
            <SectionHeading label="Impact Stories" title="Stories from the ground" />
            <div className="grid gap-8 md:grid-cols-2">
              {stories.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.08}>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {s.image && <div className="aspect-[16/9] overflow-hidden"><img src={s.image} alt={s.title} className="h-full w-full object-cover" /></div>}
                    <div className="p-7">
                      <div className="flex items-center gap-2 text-xs"><span className="overline text-gold-dark">{s.category}</span>{s.location && <span className="text-softgrey-400">· {s.location}{s.date ? `, ${s.date}` : ""}</span>}</div>
                      <h3 className="mt-2 font-serif text-2xl text-navy">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-softgrey-600">{s.body || s.summary}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/get-involved"><button className="rounded-md bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-700">Volunteer With Us</button></Link>
              <Link to="/collaborate"><button className="rounded-md border border-navy/25 px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white">Partner With Us <ArrowRight size={14} className="inline" /></button></Link>
            </div>
          </Container>
        </section>
      )}
    </div>
  );
}
